import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import Breadcrumb from "../../Breadcrumb";

const GalleryListingPage = ({ galleries }) => {
  const navigate = useNavigate();
  const [filteredGalleries, setFilteredGalleries] = useState([]);

  const getCookie = (name) => {
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      let [cookieName, cookieValue] = cookie.trim().split("=");
      if (cookieName === name) {
        return decodeURIComponent(cookieValue);
      }
    }
    return null;
  };

  const isUserAuthenticated = () => {
    return getCookie("token") || getCookie("role");
  };

  useEffect(() => {
    if (!galleries || galleries.length === 0) {
      setFilteredGalleries([]);
      return;
    }

    const userLoggedIn = isUserAuthenticated();

    const filtered = galleries.filter((gallery) => {
      if (gallery.view === "private") {
        return userLoggedIn;
      }
      return true;
    });

    setFilteredGalleries(filtered);
  }, [galleries]);

  const handleAlbumClick = (id) => {
    const encodedId = encodeURIComponent(id);
    navigate(`/gallery-details?galleryId=${encodedId}`);
  };

  return (
    <div className="py-8">
      <Breadcrumb title="Gallery" />

      <div className="mt-[40px] md:mt-[60px] lg:mt-[80px] xl:mt-[100px] container mx-auto">
        {filteredGalleries.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredGalleries.map((gallery) => (
              <AlbumCard
                key={gallery._id.toString()}
                gallery={gallery}
                handleAlbumClick={handleAlbumClick}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg">
              {!galleries || galleries.length === 0
                ? "No galleries available at the moment."
                : "No galleries found matching your access level."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AlbumCard = ({ gallery, handleAlbumClick }) => {
  const imageCount = gallery.images?.length || 0;
  const coverImage = gallery.images?.[0];

  return (
    <div
      className="group cursor-pointer transform transition-all duration-200 hover:scale-105"
      onClick={() => handleAlbumClick(gallery._id)}
    >
      <div className="relative overflow-hidden rounded-lg shadow-md group-hover:shadow-lg transition-shadow duration-200">
        <div className="aspect-square bg-gray-100">
          {coverImage ? (
            <img
              src={coverImage}
              alt={`${gallery.name} cover`}
              className="w-full h-full object-cover group-hover:brightness-95 transition-all duration-200"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <div className="text-gray-400 text-center">
                <svg
                  className="w-12 h-12 mx-auto mb-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm">No images</span>
              </div>
            </div>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-4">
          <div className="text-white">
            <h3 className="font-semibold text-lg mb-1 text-white truncate">
              {gallery.name || "Untitled Gallery"}
            </h3>
            <p className="text-sm text-gray-200">
              {imageCount === 0
                ? "Empty album"
                : `${imageCount} photo${imageCount !== 1 ? "s" : ""}`}
            </p>
          </div>
        </div>

        {gallery.view === "private" && (
          <div className="absolute top-3 right-3">
            <div className="bg-black/50 rounded-full p-2">
              <svg
                className="w-4 h-4 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryListingPage;
